import type {
  DunningStage,
  InvoiceStatus,
  SubscriptionDelinquencyState,
  SubscriptionStatus,
} from '@salonomy/types';
import type { PillTone } from '@salonomy/ui';
import { dateFormat } from '../../lib/locale.js';

/**
 * WS10 billing — DISPLAY-only label/tone helpers (brief 10 §3/§4). These map server enums to copy and
 * tones; they NEVER compute money, VAT, or proration (that is all server-derived — ADR-0010 §7). The
 * only date arithmetic here is whole-days-remaining for the trial countdown, which the lifecycle copy
 * needs but the biller does not own — it is presentational, never a billed figure.
 */

/** A plain editorial date, e.g. "12 June 2026" — for period/issue/trial boundaries. */
export function billingDate(iso: string | null): string {
  if (!iso) return '—';
  return dateFormat({
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(iso));
}

/** Whole days from now until `iso` (floored at 0). Presentational countdown for the trial prompt. */
export function daysUntil(iso: string | null): number {
  if (!iso) return 0;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86_400_000));
}

const SUBSCRIPTION_LABEL: Record<SubscriptionStatus, string> = {
  trialing: 'Trial',
  active: 'Active',
  past_due: 'Payment due',
  unpaid: 'Unpaid',
  canceled: 'Cancelled',
  incomplete: 'Setup incomplete',
  incomplete_expired: 'Setup expired',
  paused: 'Paused',
};

const SUBSCRIPTION_TONE: Record<SubscriptionStatus, PillTone> = {
  trialing: 'cobalt',
  active: 'success',
  past_due: 'danger',
  unpaid: 'danger',
  canceled: 'neutral',
  incomplete: 'neutral',
  incomplete_expired: 'neutral',
  paused: 'soft',
};

export function subscriptionLabel(status: SubscriptionStatus): string {
  return SUBSCRIPTION_LABEL[status] ?? status;
}

export function subscriptionTone(status: SubscriptionStatus): PillTone {
  return SUBSCRIPTION_TONE[status] ?? 'neutral';
}

const INVOICE_LABEL: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  open: 'Open',
  paid: 'Paid',
  uncollectible: 'Uncollectible',
  void: 'Void',
  payment_failed: 'Payment failed',
};

const INVOICE_TONE: Record<InvoiceStatus, PillTone> = {
  draft: 'neutral',
  open: 'cobalt',
  paid: 'success',
  uncollectible: 'danger',
  void: 'neutral',
  payment_failed: 'danger',
};

export function invoiceLabel(status: InvoiceStatus): string {
  return INVOICE_LABEL[status] ?? status;
}

export function invoiceTone(status: InvoiceStatus): PillTone {
  return INVOICE_TONE[status] ?? 'neutral';
}

/** The named dunning-ladder stage, in plain English (ADR-0011 §3). */
export const DUNNING_STAGE_LABEL: Record<DunningStage, string> = {
  none: 'Up to date',
  failed: 'Payment failed',
  retry: 'Retrying payment',
  grace: 'Grace period',
  read_only: 'Read-only',
};

/** The derived write-gate state → tone for the banner accent. */
export const DELINQUENCY_TONE: Record<SubscriptionDelinquencyState, PillTone> = {
  none: 'success',
  dunning: 'cobalt',
  read_only: 'danger',
};
